import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, NgZone, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ResponsiveImageResource } from '@app/core/models';
import { DeviceDetectService } from '@app/core/services';
import { ModalComponent } from '@app/core/services/modal-manager/modal-component';
import { CircularSpinnerComponent } from '@app/shared/components/circular-spinner/circular-spinner.component';
import { TranslateModule } from '@ngx-translate/core';
import { SwiperOptions } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';

@Component({
  selector: 'app-full-size-gallery',
  standalone: true,
  imports: [CommonModule, TranslateModule, CircularSpinnerComponent, SwiperModule],
  templateUrl: './full-size-gallery.component.html',
  styleUrl: './full-size-gallery.component.scss',
  animations: [trigger('fade', [state('void', style({ opacity: 0 })), transition(':enter, :leave', [animate(500)])])],
})
export class FullSizeGalleryComponent extends ModalComponent implements OnInit {
  protected images = signal<string[]>([]);
  protected currentIndex = signal<number>(0);
  protected currentImage = signal<string | null>(null);
  protected isLoading = signal<boolean>(false);
  protected swiperConfig: SwiperOptions = {
    pagination: false,
    preventClicks: false,
    navigation: false,
    preventClicksPropagation: false,
    loop: true,
    slidesPerView: 1,
    watchOverflow: false,
  };

  protected deviceDetectService = inject(DeviceDetectService);
  private zone: NgZone = inject(NgZone);

  @ViewChild('swiper') set swiper(swiper: SwiperComponent) {
    if (!swiper) {
      return;
    }
    swiper.swiperRef.slideTo(this.currentIndex() + 1, 0);
    swiper.swiperRef.on('slideChange', (ev) => {
      this.zone.run(() => {
        this.currentIndex.set(ev.realIndex);
        this.setImage();
      });
    });
  }

  ngOnInit(): void {
    this.images.set(this.extraData.images.map((image: ResponsiveImageResource) => image.max_2600x2600 ?? ''));
    this.currentIndex.set(this.extraData.index ?? 0);
    this.setImage();
  }

  previousImage(): void {
    this.currentIndex() > 0
      ? this.currentIndex.update((index) => index - 1)
      : this.currentIndex.set(this.images().length - 1);
    this.setImage();
  }

  nextImage() {
    this.currentIndex() < this.images().length - 1
      ? this.currentIndex.update((index) => index + 1)
      : this.currentIndex.set(0);
    this.setImage();
  }

  setImage() {
    this.currentImage.set(null);
    setTimeout(() => {
      this.currentImage.set(this.images()[this.currentIndex()]);
      this.isLoading.set(true);
    }, 500);
  }
}
